/**
 * Platform-aware API URL configuration for Capacitor apps
 * 
 * - Android emulator: uses 10.0.2.2 to reach host machine
 * - iOS simulator: uses localhost (works directly)
 * - Production: uses relative /api or VITE_API_URL
 * 
 * Also provides `nativeFetch()` - a wrapper that uses CapacitorHttp on native
 * platforms to bypass CORS, while Firestore uses standard fetch.
 */
import { Capacitor, CapacitorHttp, HttpResponse } from '@capacitor/core';

/**
 * Check if we should use local backend (for testing on emulators)
 * Set VITE_LOCAL_API=true in .env.local to enable
 */
function useLocalBackend(): boolean {
  return import.meta.env.VITE_LOCAL_API === 'true';
}

/**
 * Get the appropriate API base URL for the current platform
 */
export function getApiBaseUrl(): string {
  // For native platforms
  if (Capacitor.isNativePlatform()) {
    const platform = Capacitor.getPlatform();
    
    // Check if we're testing locally (VITE_LOCAL_API=true)
    if (useLocalBackend()) {
      // Android emulator uses 10.0.2.2 to access host machine's localhost
      if (platform === 'android') {
        return 'http://10.0.2.2:7071/api';
      }
      // iOS simulator can access localhost directly
      if (platform === 'ios') {
        return 'http://localhost:7071/api';
      }
    }
    
    // Production API for native platforms
    return import.meta.env.VITE_API_URL || 'https://zealous-forest-0afc9701e.4.azurestaticapps.net/api';
  }
  
  // Web: use configured URL or relative path
  return import.meta.env.VITE_API_URL || '/api';
}

/**
 * Check if we're running on a native platform (iOS/Android)
 */
export function isNativePlatform(): boolean {
  return Capacitor.isNativePlatform();
}

/**
 * Get the current platform name
 */
export function getPlatformName(): 'ios' | 'android' | 'web' {
  return Capacitor.getPlatform() as 'ios' | 'android' | 'web';
}

/**
 * Convert CapacitorHttp response to a fetch-like Response object.
 * This allows seamless use of response.json(), response.ok, etc.
 */
function httpResponseToFetchResponse(httpResponse: HttpResponse): Response {
  const body = typeof httpResponse.data === 'string' 
    ? httpResponse.data 
    : JSON.stringify(httpResponse.data);
  
  return new Response(body, {
    status: httpResponse.status,
    headers: new Headers(httpResponse.headers),
  });
}

/**
 * Native-aware fetch for API calls.
 * 
 * On native platforms (iOS/Android), uses CapacitorHttp.request() to bypass CORS.
 * On web, uses standard fetch().
 * 
 * IMPORTANT: This does NOT support streaming responses. For streaming endpoints,
 * you must use standard fetch() (which will work because CapacitorHttp is disabled globally).
 * 
 * @param url - The URL to fetch
 * @param options - Standard fetch options (method, headers, body)
 * @returns Promise<Response> - A fetch-compatible Response object
 */
export async function nativeFetch(url: string, options: RequestInit = {}): Promise<Response> {
  // On web, use standard fetch
  if (!isNativePlatform()) {
    return fetch(url, options);
  }

  // On native platforms, use CapacitorHttp to bypass CORS
  try {
    const method = (options.method || 'GET').toUpperCase();
    
    // Convert headers to plain object
    const headers: Record<string, string> = {};
    if (options.headers) {
      if (options.headers instanceof Headers) {
        options.headers.forEach((value, key) => {
          headers[key] = value;
        });
      } else if (Array.isArray(options.headers)) {
        options.headers.forEach(([key, value]) => {
          headers[key] = value;
        });
      } else {
        Object.entries(options.headers).forEach(([key, value]) => {
          if (value) headers[key] = value;
        });
      }
    }

    // Parse body if it's a string (JSON)
    let data: unknown = undefined;
    if (options.body) {
      if (typeof options.body === 'string') {
        try {
          data = JSON.parse(options.body);
        } catch {
          data = options.body;
        }
      } else {
        data = options.body;
      }
    }

    const httpResponse = await CapacitorHttp.request({
      url,
      method: method as 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
      headers,
      data,
    });

    return httpResponseToFetchResponse(httpResponse);
  } catch (error) {
    console.error('CapacitorHttp request failed:', error);
    throw error;
  }
}
